import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { differenceInMinutes } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useStoreContext } from 'features/store';
import { useGetStoreFn } from 'features/store/hooks';
import {
  getCurrentDateInStoreTimeZone,
  getRestaurantLatestClosingTime,
} from 'features/store/utils';
import { useIntl } from 'react-intl';

import { IBaseProps } from '@rbi-ctg/frontend';
import { IRestaurant } from '@rbi-ctg/store';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMainMenuContext } from 'state/menu/main-menu';
import { DAY_PART_SELECTIONS } from 'state/menu/main-menu/types';
import { ServiceMode } from 'state/service-mode';
import logger from 'utils/logger';
import { routes } from 'utils/routing';
import { isPickup } from 'utils/service-mode/service-mode';

import { ISmartAlertsContext, SmartAlert } from './types';

const MINUTES_UNTIL_BREAKFAST_DAY_PART_ENDS = 30;
const MINUTES_BEFORE_RESTAURANT_CLOSE = 30;
const MINUTES_BEFORE_PICK_UP_WINDOW_ENDS = 30;
const ORDER_SMART_ALERT_REFRESH_INTERVAL = 60_000; // 1 minute

export const SmartAlertsContext = createContext<ISmartAlertsContext>({} as ISmartAlertsContext);
export const useSmartAlertsContext = () => useContext(SmartAlertsContext);

export const useSmartAlert = (): ISmartAlertsContext => {
  const isSmartAlertsEnabled = useFlag(LaunchDarklyFlag.ENABLE_SMART_ALERTS);
  const { formatMessage } = useIntl();
  const [tsmSmartAlert, setTsmSmartAlert] = useState<SmartAlert | null>(null);
  const [orderSmartAlert, setOrderSmartAlert] = useState<SmartAlert | null>(null);
  const [currentStoreDate, setCurrentStoreDate] = useState<string | null>(null);
  const [serverOrderServiceMode, setServerOrderServiceMode] = useState<ServiceMode | null>(null);
  const [serverOrderStoreId, setServerOrderStoreId] = useState<string | null>(null);
  const orderAlertInterval = useRef<NodeJS.Timeout | null>(null);

  const { store: restaurant } = useStoreContext();
  const { activeDayParts } = useMainMenuContext();

  const getRestaurant = useGetStoreFn();

  const breakfastInterval = activeDayParts.find(
    item => item?.key === DAY_PART_SELECTIONS.BREAKFAST_KEY
  );

  const generateOrderAlert = useCallback(async () => {
    // Pick Up Window:
    // Check if the order is for a pickup service mode and the restaurant is about to close.
    // The "pickup window" refers to the time period during which a customer is allowed to pick up their order.
    // If the current time is within MINUTES_BEFORE_PICK_UP_WINDOW_ENDS minutes before the latest pickup closing time, show an alert.
    const isPickupServiceModeOrder = serverOrderServiceMode && isPickup(serverOrderServiceMode);
    if (isPickupServiceModeOrder) {
      let orderRestaurant;
      try {
        orderRestaurant = await getRestaurant(serverOrderStoreId);
      } catch (error) {
        logger.error({ error, message: 'Failed to fetch server order restaurant' });
        return;
      }

      const restaurantCurrentTime = getCurrentDateInStoreTimeZone(orderRestaurant as IRestaurant);
      const { closingTime } =
        getRestaurantLatestClosingTime({
          // For the Order alert, we must use the restaurant where the order was placed, not the currently selected restaurant
          store: orderRestaurant as IRestaurant,
          onlyPickUpServiceModes: true,
          storeLocalTime: currentStoreDate || '',
        }) || {};

      if (closingTime) {
        const minutesForClosing = differenceInMinutes(closingTime, restaurantCurrentTime, {
          roundingMethod: 'ceil',
        });

        if (minutesForClosing > 0 && minutesForClosing <= MINUTES_BEFORE_PICK_UP_WINDOW_ENDS) {
          setOrderSmartAlert({
            message: formatMessage({ id: 'pickupWindow' }, { minutes: minutesForClosing }),
          });

          return;
        }
      }
    }

    setOrderSmartAlert(null);
  }, [currentStoreDate, formatMessage, getRestaurant, serverOrderServiceMode, serverOrderStoreId]);

  const generateTsmAlert = useCallback(() => {
    if (!restaurant || !currentStoreDate) {
      setTsmSmartAlert(null);
      return;
    }

    const restaurantCurrentTime = utcToZonedTime(currentStoreDate, restaurant.timezone ?? '');

    const remainingMinutesToBreakfastDayPartChange = breakfastInterval?.endTime
      ? differenceInMinutes(breakfastInterval?.endTime, restaurantCurrentTime, {
          roundingMethod: 'ceil',
        })
      : 0;

    // Breakfast DayPart ends
    if (
      remainingMinutesToBreakfastDayPartChange > 0 &&
      remainingMinutesToBreakfastDayPartChange <= MINUTES_UNTIL_BREAKFAST_DAY_PART_ENDS
    ) {
      setTsmSmartAlert({
        message: formatMessage(
          { id: 'breakfastDayPartEnds' },
          { minutes: remainingMinutesToBreakfastDayPartChange }
        ),
        linkUrl: routes.storeLocator,
      });

      return;
    }

    // Restaurant closing time
    const { closingTime } =
      getRestaurantLatestClosingTime({
        store: restaurant,
        onlyPickUpServiceModes: true,
        storeLocalTime: currentStoreDate,
      }) || {};

    if (closingTime) {
      const minutesForClosing = differenceInMinutes(closingTime, restaurantCurrentTime, {
        roundingMethod: 'ceil',
      });

      if (minutesForClosing > 0 && minutesForClosing <= MINUTES_BEFORE_RESTAURANT_CLOSE) {
        setTsmSmartAlert({
          message: formatMessage({ id: 'restaurantCloses' }, { minutes: minutesForClosing }),
          linkUrl: routes.storeLocator,
        });

        return;
      }
    }
    setTsmSmartAlert(null);
  }, [breakfastInterval?.endTime, currentStoreDate, formatMessage, restaurant]);

  // This useEffect runs when `currentStoreDate` updates. `currentStoreDate` is updated
  // through a timer running in the `StoreTimer` component, effectively triggering
  // this useEffect whenever the store's current date changes to reflect the latest
  // time for generating alerts.
  useEffect(() => {
    if (!isSmartAlertsEnabled) {
      return;
    }

    generateTsmAlert();
  }, [generateTsmAlert, isSmartAlertsEnabled, currentStoreDate]);

  // This useEffect manages the interval for generating order alerts.
  // - It starts the interval only if `serverOrderStoreId` is present.
  // - If `serverOrderStoreId` is missing or cleared, the interval is cleared immediately.
  useEffect(() => {
    if (orderAlertInterval.current) {
      clearInterval(orderAlertInterval.current);
      orderAlertInterval.current = null;
    }

    if (!serverOrderStoreId) {
      return;
    }

    orderAlertInterval.current = setInterval(
      generateOrderAlert,
      ORDER_SMART_ALERT_REFRESH_INTERVAL
    );
    generateOrderAlert();

    return () => {
      if (orderAlertInterval.current) {
        clearInterval(orderAlertInterval.current);
        orderAlertInterval.current = null;
      }
    };
  }, [generateOrderAlert, serverOrderStoreId]);

  return {
    tsmSmartAlert,
    orderSmartAlert,
    setServerOrderServiceMode,
    setServerOrderStoreId,
    setCurrentStoreDate,
  };
};

export const SmartAlertsProvider = ({ children }: IBaseProps) => {
  const ctxValue = useSmartAlert();
  return <SmartAlertsContext.Provider value={ctxValue}>{children}</SmartAlertsContext.Provider>;
};
